import {
  Alert,
  Box,
  Button,
  CalendarIcon,
  CloseIcon,
  DateCalendar,
  IconButton,
  Stack,
  Text,
} from '@citibox-panel/ui';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import type { ExtendCustodyContext } from '../lib/extendCustodyContext';
import { extendCustodyError } from '../lib/extendCustodyError';
import { extendCustodyHeading } from '../lib/extendCustodyHeading';
import { formatLockerDay } from '../lib/lockerDay';
import type { DeliveryActionState } from '../model/deliveryActionFlow';

export interface ExtendCustodyControlProps {
  context: ExtendCustodyContext;
  // Its OWN flow instance, not the «Acciones» card's: the control lives in the Trazabilidad now, so
  // the two are physically separate and share nothing but the reducer function. `action` here is
  // either null or 'extendCustody'.
  state: DeliveryActionState;
  // The floor travels with the event, so the calendar opens on a value rather than empty.
  onStart: (date: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  // Fired once the failure has been reported to the shell's notice, so the flow's owner can put it
  // back to idle: the «Ampliar días» trigger returns while the notice stays up until dismissed.
  onFailureReported: () => void;
  onSetDate: (date: string) => void;
}

// TODO: i18n — copy en ES hasta Fase 1.
const TRIGGER_LABEL = 'Ampliar días';
const CLOSE_LABEL = 'Cerrar';
// The calendar's accessible name is STABLE and describes the control; the heading, which names the
// current delta, is a live region instead. Naming the grid with the heading would give it a name
// that changes on every selection («Ampliar 1 día» → «Ampliar 8 días») — a status masquerading as a
// label, announced twice since the same text is already on screen — while leaving the number the
// agent is actually deciding unannounced when it changes.
const CALENDAR_LABEL = 'Nueva fecha de expiración';
const NOTICE_ID = 'extend-custody';

// «Ampliar custodia», hung off the «Expira la recogida» milestone in the Trazabilidad — next to the
// datum it moves, which is the whole argument for this placement over a row in «Acciones» (SPEC D1,
// reversed 2026-08-07). One phase is visible at a time, so the control occupies one slot in the
// timeline row and never stacks: the disclosure GIVES WAY to the form, and the form to its banner.
// There is NO invalid sub-state — preloaded and bounded by minDate, the value is never empty nor
// earlier than the floor, so «Confirmar» is never disabled and no helper text is needed. It is also
// what guards against a mistaken year: with no text field, reaching 2031 takes dozens of «next
// month» clicks instead of one keystroke.
// The deadline in force is deliberately NOT repeated in the form — the timeline row this control
// hangs from IS that datum, one line above.
export function ExtendCustodyControl({
  context,
  state,
  onStart,
  onCancel,
  onConfirm,
  onFailureReported,
  onSetDate,
}: ExtendCustodyControlProps) {
  const { phase } = state;
  // Case-specific copy per error code, fail-open to the generic — the rejection causes are what the
  // agent relays to the «Usuario» on the spot. Never the backend string, and never the code either.
  // It FLOATS (ADR 0021), so the timeline row keeps its shape and the notice survives until the
  // agent dismisses it — which is what the `persistent` flag used to buy, for the specific causes
  // only.
  useReportFailure(
    NOTICE_ID,
    phase === 'error' ? extendCustodyError(state.errorCode) : null,
    onFailureReported,
  );

  if (phase === 'idle') {
    return (
      <Button
        variant="outlined"
        size="small"
        startIcon={<CalendarIcon />}
        onClick={() => {
          onStart(context.floor);
        }}
      >
        {TRIGGER_LABEL}
      </Button>
    );
  }

  if (phase === 'confirming' || phase === 'pending') {
    const pending = phase === 'pending';
    const value = state.date ?? context.floor;
    return (
      <Box
        sx={{
          maxWidth: 320,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default',
          p: 1.5,
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box role="status" sx={{ flex: 1, minWidth: 0 }}>
              <Text variant="subtitle1" component="p" bold>
                {extendCustodyHeading(context.currentExpirationDay, value)}
              </Text>
            </Box>
            {/* «×» rather than a «Cancelar» button: this panel is dismissed IN PLACE, folding back
                into the disclosure it replaced, and a full-width word next to the single
                «Confirmar» would read as the equal half of a two-choice decision. */}
            <IconButton size="small" aria-label={CLOSE_LABEL} onClick={onCancel} disabled={pending}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <DateCalendar
            value={value}
            minDate={context.floor}
            disabled={pending}
            onChange={onSetDate}
            // The disclosure that revealed this grid has just unmounted, so without autoFocus the
            // keyboard agent lands on <body> and has to re-traverse the card.
            autoFocus
            aria-label={CALENDAR_LABEL}
          />
          <Button variant="contained" fullWidth loading={pending} onClick={onConfirm}>
            Confirmar
          </Button>
        </Stack>
      </Box>
    );
  }

  if (phase === 'success') {
    // Parameterized copy: the applied date is the whole point of the banner, so it is built, not
    // looked up. A missing date is unreachable (SUCCESS preserves it) and renders nothing rather
    // than an empty green banner.
    if (state.date === null) return null;
    return <Alert severity="success">{`Custodia ampliada hasta el ${formatLockerDay(state.date)}`}</Alert>;
  }

  // Nothing for the failure: it was reported above and the flow is on its way back to idle, so the
  // milestone row goes back to offering the action instead of holding a banner.
  return null;
}
